import {
  RELEASE_SYNC_TERMINAL_STATES,
  ReleaseSyncStatus,
  useGetLastWorkspaceWorkflowRun,
  useGetReleaseById,
  useGetWorkspace,
  usePreviewReleaseDiff,
  usePromoteRelease,
  useRunReleasePlan,
  useStreamReleaseEvents,
  useStreamWorkflowRunEvents,
  useSyncRelease,
} from '@sunstone/api-client/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sunstone/ui';
import { ChevronDown, Layers, ShieldX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { ReleaseDiffDialog } from '@/components/ui/ReleaseDiffDialog';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { getReleaseActionGating } from '@/features/workspaces/releaseActionGating';
import { WorkspaceDetail } from '@/features/workspaces/WorkspaceDetail';
import { errorToastMessage, isPermissionDeniedError } from '@/lib/errors';
import { deferDialogOpen } from '@/lib/utils';

export function WorkspacePage() {
  const { tenantId, workspaceId } = useParams<{
    tenantId: string;
    workspaceId: string;
  }>();
  const { token } = useAuthToken();
  const { data: workspaceData, error: workspaceError } = useGetWorkspace(workspaceId ?? '', token);
  useStreamReleaseEvents(token);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [previewedReleaseId, setPreviewedReleaseId] = useState<string | undefined>();
  const previewDiff = usePreviewReleaseDiff(token);
  const promoteRelease = usePromoteRelease(token);
  const syncRelease = useSyncRelease(token);
  // Plan (Terraform dry-run) state - a separate, independent action from
  // Promote (see ReleaseDiffDialog's onStartPlan doc). RunReleasePlan's own
  // response already carries the deterministic temporal_workflow_id/
  // dispatched_at_ms useStreamWorkflowRunEvents needs, so no separate
  // poll-for-a-run-id step is needed before the stream can open.
  const runReleasePlan = useRunReleasePlan(token);
  // A fresh dispatch this session always wins over a resumed one - once the
  // user clicks "Plan," isPending alone suppresses the resumed source so
  // there's no flash of old output between the click and the new response
  // landing.
  const hasFreshDispatch = runReleasePlan.isPending || !!runReleasePlan.data;
  // Resumes "last plan run, if still fresh" across a page refresh - see
  // docs/design/005-release-sync-and-promote.md. Only queried while the
  // dialog is open, mirroring targetRelease's gating below. No polling:
  // WorkspaceDetail's useStreamWorkspaceEvents (already running as this
  // page's child) invalidates this query live on every workflow_runs
  // insert/status/run_url change, including a fresh dispatch picking up its
  // run_url before completion.
  const latestPlanRun = useGetLastWorkspaceWorkflowRun(
    workspaceId ?? '',
    token,
    'release_plan',
    diffDialogOpen,
  );
  // Unscoped - same query key as WorkspaceDetail's own call (rendered as
  // this page's child), so React Query dedupes onto its cache entry rather
  // than firing a second request. Used only to hide the "Promote Workspace"
  // trigger while any workflow is already running - the dialog's own
  // Plan/Promote buttons are intentionally left ungated once open.
  const lastWorkflowRun = useGetLastWorkspaceWorkflowRun(workspaceId ?? '', token);
  const isWorkflowRunning =
    lastWorkflowRun.data?.found && lastWorkflowRun.data.status === 'in_progress';
  // Sourced directly from the query, independent of hasFreshDispatch's
  // suppression below - run_url is available as soon as x-forge-installer
  // registers the run, well before completion, so it shouldn't wait for a
  // page refresh just because a fresh dispatch is suppressing the rest of
  // latestPlanRun's data.
  const planRunUrl = latestPlanRun.data?.found ? latestPlanRun.data.runUrl : undefined;
  const resumedRun =
    !hasFreshDispatch && latestPlanRun.data?.found ? latestPlanRun.data : undefined;

  const streamWorkflowId = runReleasePlan.data?.workflowId ?? resumedRun?.workflowId;
  const streamSinceMs =
    runReleasePlan.data?.dispatchedAtMs !== undefined
      ? Number(runReleasePlan.data.dispatchedAtMs)
      : resumedRun !== undefined
        ? Number(resumedRun.sinceMs)
        : undefined;
  const {
    events: planEvents,
    isComplete: planComplete,
    isTimedOut: planTimedOut,
  } = useStreamWorkflowRunEvents(streamWorkflowId, streamSinceMs, token);
  // A plan is "still planning" until it completes or the stream gives up -
  // without this, a stream that silently drops leaves the spinner running
  // forever even though the backend may have already finished (see
  // docs/design/005-release-sync-and-promote.md). The middle clause only
  // applies to a fresh dispatch (unknown terminal state yet) - a resumed
  // run's status is already known from latestPlanRun, so a resumed
  // *completed* run never flashes "running" while its stream connects.
  const isPlanning =
    runReleasePlan.isPending ||
    (!!runReleasePlan.data?.workflowId && !planComplete && !planTimedOut) ||
    (resumedRun?.status === 'in_progress' && !planComplete);
  const planError =
    runReleasePlan.error ??
    (planTimedOut
      ? new Error(
          'The plan is taking longer than expected to report back. It may still be running - check the workspace audit history or the x-forge-installer run directly.',
        )
      : null);
  // The alert can render its status/conclusion synchronously from the
  // resumed query (no need to wait on the stream) - only the actual output
  // text needs the stream's replay. A live stream event always wins once it
  // arrives, since it reflects the truly current state (e.g. a resumed
  // in-progress run that finishes while this page is open).
  const streamConclusion = planEvents.find((e) => e.eventType === 'completed')?.message;
  const planStatus = planComplete ? 'completed' : isPlanning ? 'in_progress' : resumedRun?.status;
  const planConclusion = planComplete ? streamConclusion : resumedRun?.conclusion;
  const workspace = workspaceData?.workspace;
  // The release currently shown in the diff dialog - a same-release refresh
  // has no previewedReleaseId, so it falls back to the workspace's current
  // release (matching handleSyncRelease's resolution below). Kept live
  // whenever the dialog is open, via useStreamReleaseEvents' cache
  // invalidation above, so the effect below reacts to a sync completing
  // regardless of whether it was triggered from this dialog, from "View all
  // releases," or another tab.
  const targetReleaseId = previewedReleaseId ?? workspace?.currentReleaseId;
  // PreviewReleaseDiff/PromoteRelease/RunReleasePlan all reject outright when
  // a workspace has no current_release_id (e.g. a Duplo-discovered tenant
  // whose apps.tfvars.json hasn't been backfilled yet) - guard the entry
  // point here instead of letting the operator hit that rejection as an
  // error toast.
  const noCurrentRelease = !!workspace && !workspace.currentReleaseId;
  // Shared gating for "open the promote/upgrade dialog," also used by
  // WorkspaceDetail's "Preview Upgrade to X" button - see
  // releaseActionGating.ts for why workspace.isDeprecated stays a
  // call-site-specific extra condition rather than being folded in here.
  const releaseActionGating = getReleaseActionGating(workspace, {
    isPending: previewDiff.isPending,
    noCurrentRelease,
  });
  // A plan/promote target is an upgrade whenever the dialog is previewing an
  // explicit, different release — a same-release refresh (no
  // previewedReleaseId) is never an upgrade. Drives which permission gates
  // both the Promote confirm and the Plan action below, so the two always
  // move together.
  const isPreviewingUpgrade = !!previewedReleaseId;
  const planOrPromotePermission = isPreviewingUpgrade ? 'upgrade:workspace' : 'promote:workspace';
  const targetRelease = useGetReleaseById(token, diffDialogOpen ? targetReleaseId : undefined);
  const liveRelease = targetRelease.data?.release;
  const liveSyncStatus = liveRelease?.syncStatus;
  // Identifies a specific sync *outcome*, not just its status - re-syncing
  // an already-SUCCEEDED release still produces a new lastSyncedAt, which
  // this catches even though the enum value repeats.
  const liveSyncSignature = liveRelease
    ? `${liveRelease.syncStatus}:${liveRelease.lastSyncedAt?.seconds ?? ''}`
    : undefined;
  const { hasPermission } = useAuthorization();

  const isSyncInFlight = previewDiff.isPending || promoteRelease.isPending || isPlanning;
  // Bridges the gap between the sync RPC resolving (fire-and-forget - it
  // returns almost immediately) and the SSE stream confirming
  // liveSyncStatus === IN_PROGRESS: without this, isSyncingRelease can go
  // false for a moment right after syncRelease.isPending clears, letting
  // SyncStatusAlert fall through to a stale/fallback state before "Syncing"
  // catches up. Set synchronously on click, cleared once the live status
  // actually confirms progress (or the request fails outright) - also
  // cleared on a terminal status directly, since a fast sync (or a delayed/
  // coalesced cache invalidation) can land on SUCCEEDED/FAILED without this
  // ever observing an intermediate IN_PROGRESS tick; without that, this
  // flag has no other exit and the dialog is stuck on "Waiting for the
  // release sync to complete" forever even after the sync has finished.
  const [optimisticSyncing, setOptimisticSyncing] = useState(false);
  useEffect(() => {
    if (
      liveSyncStatus === ReleaseSyncStatus.IN_PROGRESS ||
      RELEASE_SYNC_TERMINAL_STATES.has(liveSyncStatus)
    ) {
      setOptimisticSyncing(false);
    }
  }, [liveSyncStatus]);
  const isSyncingRelease =
    optimisticSyncing || syncRelease.isPending || liveSyncStatus === ReleaseSyncStatus.IN_PROGRESS;

  function resetPlanState() {
    runReleasePlan.reset();
  }

  function handlePreviewRelease(releaseId?: string) {
    if (!workspaceId) return;
    setPreviewedReleaseId(releaseId);
    setDiffDialogOpen(true);
    promoteRelease.reset();
    resetPlanState();
    setOptimisticSyncing(false);
    previewDiff.mutate(
      { workspaceId, releaseId },
      {
        onError: (err) =>
          toast.error(errorToastMessage(err, 'Failed to compute the release diff.')),
      },
    );
  }

  function handleStartPlan(runLiquibase: boolean) {
    if (!workspaceId) return;
    runReleasePlan.mutate(
      { workspaceId, releaseId: previewedReleaseId, runLiquibase },
      {
        onError: (err) => toast.error(errorToastMessage(err, 'Failed to start the release plan.')),
      },
    );
  }

  function handleSyncRelease() {
    if (!targetReleaseId) return;
    setOptimisticSyncing(true);
    syncRelease.mutate(
      { releaseId: targetReleaseId },
      {
        onError: (err) => {
          setOptimisticSyncing(false);
          toast.error(errorToastMessage(err, 'Failed to start the release sync.'));
        },
      },
    );
  }

  // Whenever the live release status (kept fresh by useStreamReleaseEvents
  // invalidating targetRelease above) settles into a *new* terminal outcome
  // while the dialog is open, re-run the diff so the sync-status alert -
  // and, on success, the diff table itself - reflect it. Covers a sync
  // triggered from this dialog's own Sync button just as well as one
  // triggered elsewhere, since both flow through the same live query.
  //
  // seenSignatureRef (not previewDiff.data) is the comparison baseline:
  // previewDiff.data is whatever the last diff call returned - on a fresh
  // open that's either undefined or stale from a *previous* release/dialog
  // session, so comparing against it fired a second, redundant
  // previewDiff.mutate immediately on every open. The ref instead records
  // the first signature observed after opening as a no-op baseline, and
  // only reacts to it *changing* afterward.
  const seenSignatureRef = useRef<string | undefined>(undefined);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omits previewDiff/previewedReleaseId
  useEffect(() => {
    if (!diffDialogOpen) {
      seenSignatureRef.current = undefined;
      return;
    }
    if (!liveSyncSignature) return;
    if (seenSignatureRef.current === undefined) {
      seenSignatureRef.current = liveSyncSignature;
      return;
    }
    if (seenSignatureRef.current === liveSyncSignature) return;
    seenSignatureRef.current = liveSyncSignature;

    const isTerminal = RELEASE_SYNC_TERMINAL_STATES.has(liveSyncStatus);
    if (isTerminal && workspaceId) {
      previewDiff.mutate({ workspaceId, releaseId: previewedReleaseId });
    }
  }, [liveSyncSignature, diffDialogOpen]);

  function handleConfirmPromote(runLiquibase: boolean) {
    if (!workspaceId) return;
    promoteRelease.mutate(
      { workspaceId, releaseId: previewedReleaseId, runLiquibase },
      {
        onSuccess: () => {
          setDiffDialogOpen(false);
          toast.success('Release promote started.');
        },
        onError: (err) =>
          toast.error(errorToastMessage(err, 'Failed to start the release promote.')),
      },
    );
  }

  function handleDiffDialogOpenChange(open: boolean) {
    if (!open && isSyncInFlight) {
      setConfirmCloseOpen(true);
      return;
    }
    if (!open) {
      resetPlanState();
    }
    setDiffDialogOpen(open);
  }

  if (isPermissionDeniedError(workspaceError)) {
    return (
      <EmptyState
        icon={<ShieldX className="h-6 w-6 text-muted-foreground" />}
        title="You don't have access to this workspace"
        description="Ask an administrator for access if you believe this is a mistake."
      />
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title={workspace?.displayName ?? '…'}
        description={
          <>
            <Layers className="h-3.5 w-3.5" />
            Workspace
          </>
        }
        action={
          <div className="flex items-center gap-2">
            {hasPermission('delete:workspace') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={!workspace}>
                    More Actions
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => deferDialogOpen(() => setDeleteOpen(true))}
                  >
                    Delete Workspace
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!releaseActionGating.disabled &&
              !workspace?.isDeprecated &&
              !isWorkflowRunning &&
              hasPermission('promote:workspace') && (
                <Button size="sm" onClick={() => handlePreviewRelease()}>
                  Promote Workspace
                </Button>
              )}
          </div>
        }
      />
      {workspaceId && (
        <WorkspaceDetail
          workspaceId={workspaceId}
          tenantId={tenantId}
          deleteOpen={deleteOpen}
          onDeleteOpenChange={setDeleteOpen}
          onPreviewRelease={handlePreviewRelease}
          isPreviewPending={previewDiff.isPending}
        />
      )}
      <ReleaseDiffDialog
        open={diffDialogOpen}
        onOpenChange={handleDiffDialogOpenChange}
        isPending={previewDiff.isPending}
        data={previewDiff.data}
        liveTargetRelease={liveRelease}
        requestError={previewDiff.error}
        isUpgrade={isPreviewingUpgrade}
        sync={{
          onSyncRelease: hasPermission('sync:release') ? handleSyncRelease : undefined,
          isSyncingRelease,
          syncReleaseError: syncRelease.error,
        }}
        confirm={
          // Omitting the whole prop (not just onConfirmUpgrade) keeps the
          // dialog read-only per its own doc comment — onConfirmUpgrade is
          // required within ReleaseDiffDialogConfirmProps precisely so a
          // caller can't pass it as undefined by mistake.
          hasPermission(planOrPromotePermission)
            ? {
                onConfirmUpgrade: handleConfirmPromote,
                isConfirming: promoteRelease.isPending,
                confirmError: promoteRelease.error,
                isUpgrade: isPreviewingUpgrade,
              }
            : undefined
        }
        plan={{
          onStartPlan: hasPermission(planOrPromotePermission) ? handleStartPlan : undefined,
          isPlanning,
          events: planEvents,
          error: planError,
          status: planStatus,
          conclusion: planConclusion,
          completedAt: resumedRun?.completedAt,
          runUrl: planRunUrl,
          timedOut: planTimedOut,
        }}
      />
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sync still running</AlertDialogTitle>
            <AlertDialogDescription>
              A release sync or promote is still running in the background and will continue even if
              you close this dialog. You can check back later — progress is tracked on this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep watching</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmCloseOpen(false);
                resetPlanState();
                setDiffDialogOpen(false);
              }}
            >
              Close anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
